import "./products.css";
import "../services/service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";

/* ─── SVG icon helpers ───────────────────────────────────────────────────── */
const Icon = ({ d, d2, d3, circle, rect, poly, line, ...rest }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {circle && <circle {...circle} />}
    {rect &&
      (Array.isArray(rect) ? (
        rect.map((r, i) => <rect key={i} {...r} />)
      ) : (
        <rect {...rect} />
      ))}
    {poly &&
      (Array.isArray(poly) ? (
        poly.map((p, i) => <polyline key={i} points={p} />)
      ) : (
        <polyline points={poly} />
      ))}
    {line &&
      (Array.isArray(line) ? (
        line.map((l, i) => <line key={i} {...l} />)
      ) : (
        <line {...line} />
      ))}
    {d && <path d={d} />}
    {d2 && <path d={d2} />}
    {d3 && <path d={d3} />}
  </svg>
);

/* ─── Bento feature cells ─────────────────────────────────────────────────── */
const bentoFeatures = [
  {
    size: "wide",
    accent: true,
    icon: {
      d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
      d2: "M9 22V12h6v10",
    },
    title: "Drag-and-Drop Form Builder",
    desc: "Create forms visually without writing complex frontend code. Drag fields onto the canvas, configure them inline, and publish instantly.",
    tags: [
      "Visual Builder",
      "Dynamic Fields",
      "Form Templates",
      "Responsive Layouts",
    ],
  },
  {
    size: "med",
    icon: {
      d: "M12 2L2 7l10 5 10-5-10-5z",
      d2: "M2 17l10 5 10-5",
      d3: "M2 12l10 5 10-5",
    },
    title: "Centralized Data Management",
    desc: "Manage and organize form submissions and operational data from one centralized platform.",
  },
  {
    size: "third",
    icon: {
      d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
      d2: "M14 2v6h6",
      line: [
        { x1: "16", y1: "13", x2: "8", y2: "13" },
        { x1: "16", y1: "17", x2: "8", y2: "17" },
      ],
    },
    title: "Ready-Made Templates",
    desc: "Use prebuilt templates to speed up form creation and workflow setup.",
  },
  {
    size: "third",
    icon: {
      rect: [
        { x: "2", y: "2", width: "9", height: "9", rx: "1" },
        { x: "13", y: "2", width: "9", height: "9", rx: "1" },
        { x: "13", y: "13", width: "9", height: "9", rx: "1" },
        { x: "2", y: "13", width: "9", height: "9", rx: "1" },
      ],
    },
    title: "Secure Sharing & Collaboration",
    desc: "Share forms securely with teams, users, and customers using links or QR codes.",
  },
  {
    size: "third",
    icon: {
      d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
      line: { x1: "9", y1: "10", x2: "15", y2: "10" },
    },
    title: "Data Export & Reporting",
    desc: "Export and manage collected data whenever needed for operational and reporting purposes.",
  },
];

/* ─── How it works steps ─────────────────────────────────────────────────── */
const steps = [
  {
    num: "01",
    title: "Design Your Layout",
    desc: "Open the visual canvas and drag components onto the page. Arrange columns, grids, and panels without writing markup.",
  },
  {
    num: "02",
    title: "Connect Your Data",
    desc: "Bind API endpoints or local data sources to any component. Map fields using the visual data mapper — no code.",
  },
  {
    num: "03",
    title: "Configure Logic",
    desc: "Set conditional visibility, validations, role permissions, and workflow triggers through the config editor.",
  },
  {
    num: "04",
    title: "Launch Instantly",
    desc: "Click publish. UniBuilder generates optimised output and deploys your application live — in seconds.",
  },
];

/* ─── Use cases ──────────────────────────────────────────────────────────── */
const useCases = [
  {
    icon: {
      rect: { x: "2", y: "3", width: "20", height: "14", rx: "2" },
      line: [
        { x1: "8", y1: "21", x2: "16", y2: "21" },
        { x1: "12", y1: "17", x2: "12", y2: "21" },
      ],
    },
    title: "Business Forms",
    desc: "Create and manage business forms for data collection, approvals, and operational workflows.",
    bullets: [
      "Drag-and-drop creation",
      "Dynamic field configuration",
      "Form submission tracking",
    ],
  },
  {
    icon: {
      d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
      circle: { cx: "9", cy: "7", r: "4" },
      d2: "M23 21v-2a4 4 0 0 0-3-3.87",
      d3: "M16 3.13a4 4 0 0 1 0 7.75",
    },
    title: "Workflow Management",
    desc: "Build and manage operational workflows, approval processes, and team collaboration systems.",
    bullets: ["Approval workflows", "Process management", "Team collaboration"],
  },
  {
    icon: {
      d: "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z",
      d2: "M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16",
    },
    title: "Data Collection",
    desc: "Collect, organize, and centralize form submissions and operational data from one platform.",
    bullets: [
      "Centralized data storage",
      "Structured management",
      "Easy data access",
    ],
  },
  {
    icon: { poly: ["13 2 3 14 12 14 11 22 21 10 12 10 13 2"] },
    title: "Business Automation",
    desc: "Automate repetitive operational tasks and streamline internal business processes.",
    bullets: ["Operational tracking", "Task automation", "Faster execution"],
  },
];

/* ─── Capabilities ────────────────────────────────────────────────────────── */
const capabilities = [
  {
    icon: {
      circle: { cx: "12", cy: "12", r: "3" },
      d: "M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83",
    },
    label: "Faster Workflow Management",
    sub: "Simplifies form handling and reduces manual processes",
  },
  {
    icon: { d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
    label: "Easy Low-Code Platform",
    sub: "Minimal coding required with user-friendly interface",
  },
  {
    icon: {
      rect: [
        { x: "3", y: "3", width: "7", height: "7" },
        { x: "14", y: "3", width: "7", height: "7" },
        { x: "14", y: "14", width: "7", height: "7" },
        { x: "3", y: "14", width: "7", height: "7" },
      ],
    },
    label: "Better Data Organization",
    sub: "Centralized records and organized form submissions",
  },
  {
    icon: { poly: ["23 6 13.5 15.5 8.5 10.5 1 18"], d: "M17 6h6v6" },
    label: "Better Team Collaboration",
    sub: "Share workflows securely across teams and departments",
  },
];

/* ─── Stats ───────────────────────────────────────────────────────────────── */
const stats = [
  { value: "10×", label: "Faster Delivery" },
  { value: "70%", label: "Less Code Written" },
  { value: "4 days", label: "Average MVP Time" },
  { value: "100%", label: "Config-Driven" },
];

/* ════════════════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════════════════ */
export default function Unibuilder() {
  return (
    <>
      <SEO
        title="UniBuilder: AI Workflow & App Platform | Best Code Technology"
        description="UniBuilder helps build AI workflows, automation tools and code generation platforms."
        keywords="AI builder, workflow automation, code generator"
        url="https://bestcodetechnology.in/products/unibuilder"
      />

      <div className="uni-page">
        {/* ── HERO ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <h1 className="svc-hero__headline">
                  Build and Manage <span>Business Workflows</span>
                </h1>

                <p className="svc-hero__desc">
                  UniBuilder is a low-code platform designed to simplify form
                  creation, workflow management, and centralized data handling
                  for businesses and operational teams.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── BENTO FEATURES ── */}
        <section className="uni-bento">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <h2 className="svc-section-title mt-3">
                  Core Features of UniBuilder
                </h2>
                <p className="svc-section-sub">
                  UniBuilder helps teams build forms, manage workflows, organize
                  data, and streamline operations using a simple low-code
                  approach.
                </p>
              </div>
            </div>

            <div className="uni-bento__grid">
              {bentoFeatures.map((f, i) => (
                <div
                  key={i}
                  className={[
                    "uni-bento__cell",
                    `uni-bento__cell--${f.size}`,
                    f.accent ? "uni-bento__cell--accent" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="uni-bento__cell-icon">
                    <Icon {...f.icon} />
                  </div>
                  <h4 className="uni-bento__cell-title">{f.title}</h4>
                  <p className="uni-bento__cell-desc">{f.desc}</p>
                  {f.tags && (
                    <div className="uni-bento__tags">
                      {f.tags.map((t) => (
                        <span key={t} className="uni-bento__tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="uni-steps">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">How It Works</span>
                <h2 className="svc-section-title mt-3">
                  From Idea to Live App in Four Steps
                </h2>
                <p className="svc-section-sub">
                  No complex setup, no steep learning curve. UniBuilder's guided
                  flow takes you from blank canvas to deployed application
                  faster than any traditional approach.
                </p>
              </div>
            </div>

            <div className="row g-4 uni-steps__row">
              {steps.map((s) => (
                <div key={s.num} className="col-12 col-sm-6 col-lg-3">
                  <div className="uni-step">
                    <div className="uni-step__num">{s.num}</div>
                    <h4 className="uni-step__title">{s.title}</h4>
                    <p className="uni-step__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── USE CASES ── */}
        <section className="uni-cases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">Use Cases</span>
                <h2 className="svc-section-title mt-3">
                  What Teams Build with UniBuilder
                </h2>
                <p className="svc-section-sub">
                  UniBuilder can be used across multiple operational and
                  business workflows.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {useCases.map((uc, i) => (
                <div key={i} className="col-12 col-md-6 col-xl-3">
                  <div className="uni-case-card">
                    <div className="uni-case-card__icon">
                      <Icon {...uc.icon} />
                    </div>
                    <h4 className="uni-case-card__title">{uc.title}</h4>
                    <p className="uni-case-card__desc">{uc.desc}</p>
                    <ul className="uni-case-card__list">
                      {uc.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CAPABILITIES ── */}
        <section className="uni-capabilities">
          <div className="container-xl">
            <div className="row align-items-center g-5">
              {/* left copy */}
              <div className="col-12 col-lg-5">
                <span className="pill-label">Advanced Capabilities</span>
                <h2 className="svc-section-title mt-3">
                  Why Businesses Use UniBuilder
                </h2>
                <p className="svc-section-sub svc-section-sub--left">
                  UniBuilder simplifies workflow management and helps teams
                  reduce manual operational work.
                </p>
              </div>

              {/* right visual panel */}
              <div className="col-12 col-lg-7">
                <div className="uni-cap-visual">
                  {capabilities.map((cap, i) => (
                    <div key={i} className="uni-cap-row">
                      <div className="uni-cap-row__icon">
                        <Icon {...cap.icon} />
                      </div>
                      <div>
                        <p className="uni-cap-row__label">{cap.label}</p>
                        <p className="uni-cap-row__sub">{cap.sub}</p>
                      </div>
                      <span className="uni-cap-row__badge">Included</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA BAND ──
        <section className="uni-cta">
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8">
                <span className="pill-label">Get Started Today</span>
                <h2 className="uni-cta__headline">
                  Ready to ship your next app
                  <br />
                  <span>in days, not months?</span>
                </h2>
                <p className="uni-cta__sub">
                  Join teams already using UniBuilder to eliminate repetitive
                  development work and focus on what actually matters — the
                  product experience.
                </p>
                <div className="uni-cta__actions">
                  <a href="/contact" className="btn btn-primary-custom btn-lg">
                    Request Early Access
                  </a>
                  <a href="/products/unibuilder" className="btn btn-ghost btn-lg">
                    Browse All Products
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section> */}
      </div>
    </>
  );
}
